import React, { useEffect, useRef, useState } from 'react';
import api from '../api/axiosConfig';
import { usePublicLanguage } from '../contexts/PublicLanguageContext';
import './GoogleSignInButton.css';

// "Sign in with Google" via Google Identity Services. The client ID is
// fetched from the backend at runtime (not baked at build), so enabling or
// changing it needs no frontend rebuild. If the backend returns no client
// ID, the button simply doesn't render.

interface GoogleSignInButtonProps {
    onSuccess: () => void;
    onError?: (message: string) => void;
}

const GIS_SRC = 'https://accounts.google.com/gsi/client';

// Load the GIS script once, shared across mounts. The failure message is
// passed in so it can be translated; the promise is memoised, so it is the
// first caller's language that names a failure.
let gisScriptPromise: Promise<void> | null = null;
const loadGisScript = (failureMessage: string): Promise<void> => {
    if ((window as any).google?.accounts?.id) return Promise.resolve();
    if (gisScriptPromise) return gisScriptPromise;
    gisScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = GIS_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(failureMessage));
        document.head.appendChild(script);
    });
    return gisScriptPromise;
};

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({ onSuccess, onError }) => {
    const { t } = usePublicLanguage();
    const buttonRef = useRef<HTMLDivElement>(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const handleCredential = async (response: { credential?: string }) => {
            if (!response.credential) return;
            try {
                const res = await api.post('/auth/google/', { credential: response.credential });
                localStorage.setItem('token', res.data.token);
                onSuccess();
            } catch (err: any) {
                const message = err.response?.data?.detail || t.auth.googleSignInFailed;
                onError?.(message);
            }
        };

        (async () => {
            try {
                const { data } = await api.get('/auth/google/config/');
                const clientId = data.client_id;
                if (!clientId || cancelled) return;

                await loadGisScript(t.auth.googleLoadFailed);
                if (cancelled || !buttonRef.current) return;

                const google = (window as any).google;
                google.accounts.id.initialize({
                    client_id: clientId,
                    callback: handleCredential,
                });
                google.accounts.id.renderButton(buttonRef.current, {
                    theme: 'outline',
                    size: 'large',
                    width: 320,
                    text: 'continue_with',
                    logo_alignment: 'center',
                });
                setReady(true);
            } catch {
                // Sign-in unavailable (not configured, or script blocked) —
                // leave the button hidden; email/password login still works.
            }
        })();

        return () => { cancelled = true; };
    }, [onSuccess, onError, t]);

    // The ref'd container is always mounted (even before `ready`) so that
    // buttonRef.current exists when the effect runs and renderButton has a
    // target. Until Google has rendered into it, the wrapper is parked
    // offscreen so it leaves no visible gap; `ready` then reveals it in flow.
    return (
        <div className={`google-signin${ready ? '' : ' google-signin--pending'}`}>
            <div className="google-signin-divider"><span>{t.auth.orDivider}</span></div>
            <div ref={buttonRef} className="google-signin-button" />
        </div>
    );
};

export default GoogleSignInButton;
